/**
 * RevenueChart Component - T141
 *
 * Bar chart showing revenue by month with Y-axis
 */
import { useState } from 'react';
import { GlassPanel } from '../ui';
import { formatCurrency } from '@armard/shared';
import { useRevenueByPeriod } from '../../hooks/useDashboard';

interface RevenueChartProps {
  months?: number;
  className?: string;
}

const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

function formatPeriod(period: string): string {
  const [, month] = period.split('-');
  return monthNames[parseInt(month, 10) - 1] || period;
}

function formatAxisValue(value: number): string {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(0)}k`;
  }
  return value.toFixed(0);
}

export function RevenueChart({ months = 12, className = '' }: RevenueChartProps) {
  const { data, isLoading, error } = useRevenueByPeriod(months);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (isLoading) {
    return (
      <GlassPanel className={`p-4 ${className}`}>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-white/10 rounded w-1/4" />
          <div className="h-48 bg-white/5 rounded" />
        </div>
      </GlassPanel>
    );
  }

  if (error || !data) {
    return (
      <GlassPanel className={`p-4 ${className}`}>
        <p className="text-red-400 text-center py-8">Fehler beim Laden der Umsatzdaten</p>
      </GlassPanel>
    );
  }

  // Include previous year in max calculation for proper scaling
  const maxRevenue = Math.max(
    ...data.map((d) => d.revenue),
    ...data.map((d) => d.prevYearRevenue || 0),
    1
  );
  const totalRevenue = data.reduce((sum, d) => sum + d.revenue, 0);
  const prevYearTotal = data.reduce((sum, d) => sum + (d.prevYearRevenue || 0), 0);

  // Calculate nice axis values
  const axisSteps = 4;
  const rawStep = maxRevenue / axisSteps;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const niceStep = Math.ceil(rawStep / magnitude) * magnitude;
  const axisMax = niceStep * axisSteps;
  const axisValues = Array.from({ length: axisSteps + 1 }, (_, i) => i * niceStep);

  const chartHeight = 180;

  return (
    <GlassPanel className={`p-4 ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-medium text-white">Umsatz</h3>
        <div className="flex items-center gap-4 text-xs text-white/50">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500/70" />
            {new Date().getFullYear()}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-white/10" />
            {new Date().getFullYear() - 1}
          </span>
        </div>
      </div>

      {/* Total with YoY comparison */}
      <div className="mb-6">
        <p className="text-2xl font-semibold text-white">{formatCurrency(totalRevenue)}</p>
        {prevYearTotal > 0 && (
          <p className="text-xs text-white/40 mt-1">
            Vorjahr: {formatCurrency(prevYearTotal)}
            {totalRevenue !== prevYearTotal && (
              <span className={totalRevenue > prevYearTotal ? 'text-emerald-400 ml-2' : 'text-red-400 ml-2'}>
                {totalRevenue > prevYearTotal ? '+' : ''}
                {(((totalRevenue - prevYearTotal) / prevYearTotal) * 100).toFixed(0)}%
              </span>
            )}
          </p>
        )}
      </div>

      {/* Chart with Y-axis */}
      <div className="flex gap-2" style={{ height: `${chartHeight}px` }}>
        {/* Y-axis */}
        <div className="flex flex-col justify-between text-right pr-2 w-10 shrink-0">
          {[...axisValues].reverse().map((val) => (
            <span key={val} className="text-xs text-white/30 leading-none">
              {formatAxisValue(val)}
            </span>
          ))}
        </div>

        {/* Chart area */}
        <div className="flex-1 relative border-l border-b border-white/10">
          {/* Horizontal grid lines */}
          {axisValues.map((val) => (
            <div
              key={`grid-${val}`}
              className="absolute left-0 right-0 border-t border-white/5"
              style={{ bottom: `${(val / axisMax) * 100}%` }}
            />
          ))}

          {/* Bars */}
          <div className="absolute inset-0 flex items-end gap-1 px-1">
            {data.map((item, i) => {
              const heightPercent = axisMax > 0 ? (item.revenue / axisMax) * 100 : 0;
              const prevYearHeightPercent = axisMax > 0 ? ((item.prevYearRevenue || 0) / axisMax) * 100 : 0;
              const isHovered = hoveredIndex === i;
              const hasComparison = (item.prevYearRevenue || 0) > 0;

              return (
                <div
                  key={item.period}
                  className="flex-1 h-full relative"
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  {/* Previous year bar (soft, behind) */}
                  {hasComparison && (
                    <div
                      className="absolute bottom-0 left-0 right-0 rounded-t bg-white/10"
                      style={{ height: `${Math.max(prevYearHeightPercent, 1)}%` }}
                    />
                  )}

                  {/* Current year bar */}
                  <div
                    className={`absolute bottom-0 left-0 right-0 rounded-t transition-all cursor-pointer ${
                      isHovered ? 'bg-emerald-400' : 'bg-emerald-500/70'
                    }`}
                    style={{ height: `${Math.max(heightPercent, 2)}%` }}
                  />

                  {/* Tooltip */}
                  {isHovered && (
                    <div
                      className="absolute left-1/2 -translate-x-1/2 px-2 py-1 bg-zinc-800 border border-white/10 rounded text-xs text-white whitespace-nowrap pointer-events-none z-10"
                      style={{ bottom: `${Math.max(heightPercent, prevYearHeightPercent) + 5}%` }}
                    >
                      <div className="font-medium">{formatCurrency(item.revenue)}</div>
                      <div className="text-white/50">
                        {item.invoiceCount} {item.invoiceCount === 1 ? 'Rechnung' : 'Rechnungen'}
                      </div>
                      {hasComparison && (
                        <div className="text-white/30 mt-1 pt-1 border-t border-white/10">
                          Vorjahr: {formatCurrency(item.prevYearRevenue || 0)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* X-axis labels */}
      <div className="flex gap-2 mt-2">
        <div className="w-10 shrink-0" /> {/* Spacer for Y-axis */}
        <div className="flex-1 flex">
          {data.map((item) => (
            <div key={item.period} className="flex-1 text-center">
              <span className="text-xs text-white/40">{formatPeriod(item.period)}</span>
            </div>
          ))}
        </div>
      </div>
    </GlassPanel>
  );
}
